import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, Paperclip, Smile } from "lucide-react";

export default function Chatbot({ flowConfig }) {
  const [messages, setMessages] = useState([
    { user: false, text: flowConfig?.autoMessage || "Welcome! How can I help you?" },
  ]);
  const [userInput, setUserInput] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages([{ user: false, text: flowConfig?.autoMessage || "Welcome! How can I help you?" }]);
  }, [flowConfig?.autoMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const getBotReply = (userText) => {
    if (!userText) return "";
    if (!flowConfig?.responses || !Array.isArray(flowConfig.responses)) {
      return "Sorry, I'm not configured properly. Please try again later.";
    }
    const match = flowConfig.responses.find(
      (r) => r?.trigger && r.trigger.toLowerCase() === userText.trim().toLowerCase()
    );

    if (!match) {
      const defaultMsg = flowConfig.responses.find((r) => r?.action === "default");
      return defaultMsg?.content || "Sorry, I didn't understand that. Please try again.";
    }

    switch (match.action) {
      case "document":
        return `📄 Document Link: ${match.content || ""}`;
      case "message":
      default:
        return match.content || "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = userInput.trim();
    if (!trimmed) return;
    setMessages((prev) => [...prev, { user: true, text: trimmed }]);
    setUserInput("");

    setTimeout(() => {
      const reply = getBotReply(trimmed);
      setMessages((prev) => [...prev, { user: false, text: reply }]);
    }, 600);
  };

  const formatTime = () => {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div 
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#efeae2'
      }}
    >
      {/* Messages Area - Scrollable */}
      <div 
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          overflowY: 'auto',
          padding: '1rem',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4d4d4' fill-opacity='0.1'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        <div className="max-w-4xl mx-auto space-y-3">
          {messages.map((msg, idx) => {
            const messageText = String(msg?.text || "");
            const isDocumentLink = messageText.startsWith("📄 Document Link: ");
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: msg.user ? 20 : -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${msg.user ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`
                    max-w-[85%] sm:max-w-[70%] px-3 py-2 rounded-lg shadow-sm relative
                    ${msg.user 
                      ? "bg-[#dcf8c6] text-gray-800 rounded-br-none" 
                      : "bg-white text-gray-800 rounded-bl-none border border-gray-100"
                    }
                  `}
                >
                  <div className="break-words text-sm leading-relaxed">
                    {isDocumentLink ? (
                      <a
                        href={messageText.replace("📄 Document Link: ", "")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#25d366] hover:text-[#128c7e] underline"
                      >
                        📄 {messageText.replace("📄 Document Link: ", "") || "Download Document"}
                      </a>
                    ) : (
                      messageText
                    )}
                  </div>
                  
                  <div className={`text-xs text-gray-500 mt-1 text-right`}>
                    {formatTime()}
                    {msg.user && (
                      <span className="ml-1 text-[#53bdeb]">✓✓</span>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - Fixed at bottom */}
      <div 
        style={{
          padding: '0.75rem 1rem',
          backgroundColor: '#f0f2f5',
          borderTop: '1px solid #e5e7eb',
          flexShrink: 0
        }}
      >
        <form
          onSubmit={handleSubmit}
          className="flex items-end gap-2 max-w-4xl mx-auto"
        >
          <div className="flex-1 bg-white rounded-full px-4 py-2 border border-gray-200 focus-within:border-[#25d366] transition-colors">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <Smile className="w-5 h-5" />
              </button>
              
              <input
                type="text"
                className="flex-1 border-none outline-none bg-transparent text-sm placeholder-gray-500 py-1"
                placeholder="Type a message"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                autoComplete="off"
              />
              
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <Paperclip className="w-5 h-5" />
              </button>
            </div>
          </div>

          <motion.button
            type="submit"
            className="bg-[#25d366] text-white p-3 rounded-full hover:bg-[#128c7e] transition-colors shadow-lg"
            whileTap={{ scale: 0.95 }}
            disabled={!userInput.trim()}
          >
            <Send className="w-5 h-5" />
          </motion.button>
        </form>
      </div>
    </div>
  );
}
